import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ForumIcon from '@mui/icons-material/Forum';
import SettingsIcon from '@mui/icons-material/Settings';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { Funnel, NodeVariants } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, useReactFlow } from 'reactflow';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import { AutoReply, AutoReplyCase, Offer } from '../../campaign-builder.types';
import { coerceOfferFieldToString, getOfferFieldInUse } from '../../offer-field.utils';
import AiGeneratedMessaging from './ai-generated-messaging';
import AutoReplySettings from './auto-reply-Settings';
import CommunicationSettings from './communication-settings';
import FlowBuilder from './flow-builder/flow-builder';
import UpdateAiBriefing from './update-ai-briefing';

interface Props {
  campaignId: string;
  isFlowChanging: boolean;
  isUpdating: boolean;

  linkedinAccount: LinkedinAccount | undefined;

  isFormal: boolean;
  includeEmoji: boolean;
  swissS: boolean;
  language: string;
  secondaryLanguage: string;

  productGroup: ProductGroups;
  offer: Offer;
  customPlaceholders: import('../../campaign-builder.types').CustomPlaceholder[];

  autoReply: AutoReply;

  handleSelectNodeFromList(node: Node): () => void;
  handleUnselectNodeFromList(): void;
  handleOpenFlowCreationDialog(): void;
  /** Flow-Builder (Schritt 3): the page replaces campaign + canvas with the generated flow. */
  handleFlowBuilderApplied(campaign: Funnel): void;

  handleOpenChangeCommunicationStyleWarningDialog(): void;
  handleIncludeEmojiChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleSwissSChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleSecondaryLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onIsActiveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when user changes the goal text. Accepts a textfield event. */
  onGoalChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when the user updates the entire array of cases. */
  onCaseHandlingUpdate(newCaseHandling: AutoReplyCase[]): void;

  /** Called to save or finalize the auto-reply settings. (Typically no event needed.) */
  handleUpdateAutoReply(): void;
  handleOfferInputChange(type: keyof Offer): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOfferToggleChange(type: keyof Offer): (inUse: boolean) => void;

  handleAddCustomPlaceholder(key: string, value: string): void;
  handleDeleteCustomPlaceholder(key: string): void;
  handleEditCustomPlaceholder(originalKey: string, newKey: string, newValue: string): void;
}

const CampaignFlowManager: React.FC<Props> = (props) => {
  const {
    campaignId,
    isFlowChanging,
    isUpdating,
    linkedinAccount,
    isFormal,
    includeEmoji,
    swissS,
    language,
    secondaryLanguage,
    productGroup,
    offer,
    customPlaceholders,
    handleSelectNodeFromList,
    handleUnselectNodeFromList,
    handleOpenFlowCreationDialog,
    handleFlowBuilderApplied,
    handleOpenChangeCommunicationStyleWarningDialog,
    handleIncludeEmojiChange,
    handleSwissSChange,
    handleLanguageChange,
    handleSecondaryLanguageChange,
    autoReply,
    onIsActiveChange,
    onGoalChange,
    onCaseHandlingUpdate,
    handleUpdateAutoReply,
    handleOfferInputChange,
    handleOfferToggleChange,
    handleAddCustomPlaceholder,
    handleDeleteCustomPlaceholder,
    handleEditCustomPlaceholder,
  } = props;

  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { getNodes } = useReactFlow();
  const nodes = getNodes();

  const { selectedNode, setSelectedNode } = useCampaignNodeStore((state) => ({
    selectedNode: state.selectedNode,
    setSelectedNode: state.setSelectedNode,
  }));

  // Check if the selected node is a FUNCTION_NODE
  const isNodeSelected = !_.isNil(selectedNode) && selectedNode.type === NodeVariants.FUNCTION_NODE;

  // Collapsible section states
  const [communicationStyleSettingsOpen, setCommunicationStyleSettingsOpen] = useState(false);
  const [aiGeneratedMessagingOpen, setAiGeneratedMessagingOpen] = useState(false);
  const [aiBriefingOpen, setAiBriefingOpen] = useState(false);

  const [autoReplySettingsOpen, setAutoReplySettingsOpen] = useState(false);

  const normalizedOffer: Offer = {
    jobTitle: coerceOfferFieldToString(offer?.jobTitle),
    jobOffer: coerceOfferFieldToString(offer?.jobOffer),
    problems: coerceOfferFieldToString(offer?.problems),
    features: coerceOfferFieldToString(offer?.features),
    benefits: coerceOfferFieldToString(offer?.benefits),
    impact: coerceOfferFieldToString(offer?.impact),
  };

  const fieldInUse = {
    jobTitle: getOfferFieldInUse(offer?.jobTitle),
    jobOffer: getOfferFieldInUse(offer?.jobOffer),
    problems: getOfferFieldInUse(offer?.problems),
    features: getOfferFieldInUse(offer?.features),
    benefits: getOfferFieldInUse(offer?.benefits),
    impact: getOfferFieldInUse(offer?.impact),
  };

  /**
   * Open the AI Generated Messaging section if a FUNCTION_NODE is selected
   */
  useEffect(() => {
    if (isNodeSelected) {
      setAiGeneratedMessagingOpen(true);
    }
  }, [selectedNode, isNodeSelected]);

  /**
   * If there's at least one FUNCTION_NODE with a functionName,
   * open the AI Generated Messaging section by default.
   * (i.e., if the child "has messages")
   */
  useEffect(() => {
    const hasMessageNodes = nodes.some((node) => node.type === NodeVariants.FUNCTION_NODE && node.data.functionName);
    if (hasMessageNodes && !aiGeneratedMessagingOpen) {
      setAiGeneratedMessagingOpen(true);
    }
  }, [nodes]);

  /**
   * Clean up: unselect node on component unmount
   */
  useEffect(() => {
    return () => {
      setSelectedNode(null);
    };
  }, []);

  return (
    <div>
      <FlowBuilder
        campaignId={campaignId}
        productGroup={productGroup}
        busy={isFlowChanging || isUpdating}
        nodes={nodes}
        linkedinAccount={linkedinAccount}
        customPlaceholders={customPlaceholders}
        fieldInUse={fieldInUse}
        handleSelectNodeFromList={handleSelectNodeFromList}
        handleUnselectNodeFromList={handleUnselectNodeFromList}
        onApplied={handleFlowBuilderApplied}
        onOpenAutoReplySettings={() => setAutoReplySettingsOpen(true)}
        onOpenFlowCreationDialog={handleOpenFlowCreationDialog}
      />

      {/* Communication Settings */}
      <div className={cx(classes.section, communicationStyleSettingsOpen ? 'expanded' : undefined)}>
      <Stack
        className={classes.sectionHeader}
        direction={'row'}
        alignContent={'center'}
        justifyContent={'space-between'}
        onClick={() => setCommunicationStyleSettingsOpen((prev) => !prev)}
      >
        <Stack direction={'row'} alignContent={'center'} gap={2}>
          <SettingsIcon className={classes.icon} />
          <Typography className={classes.title}>{t('communicationStyleSettings')}</Typography>
        </Stack>
        <IconButton className={cx(classes.expandButton, communicationStyleSettingsOpen ? 'expanded' : undefined)} size={'small'}>
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={communicationStyleSettingsOpen} unmountOnExit={true}>
        <div className={classes.sectionContent}>
        <CommunicationSettings
          isFormal={isFormal}
          includeEmoji={includeEmoji}
          swissS={swissS}
          language={language}
          secondaryLanguage={secondaryLanguage}
          handleOpenFlowCreationDialog={handleOpenFlowCreationDialog}
          handleOpenChangeCommunicationStyleWarningDialog={handleOpenChangeCommunicationStyleWarningDialog}
          handleIncludeEmojiChange={handleIncludeEmojiChange}
          handleSwissSChange={handleSwissSChange}
          handleLanguageChange={handleLanguageChange}
          handleSecondaryLanguageChange={handleSecondaryLanguageChange}
        />
        </div>
      </Collapse>
      </div>

      {/* AI Generated Messaging */}
      <div className={cx(classes.section, aiGeneratedMessagingOpen ? 'expanded' : undefined)}>
      <Stack
        className={classes.sectionHeader}
        direction={'row'}
        alignContent={'center'}
        justifyContent={'space-between'}
        onClick={() => setAiGeneratedMessagingOpen((prev) => !prev)}
      >
        <Stack direction={'row'} alignContent={'center'} gap={2}>
          <ForumIcon className={classes.icon} />
          <Typography className={classes.title}>{t('aiGeneratedMessaging')}</Typography>
        </Stack>
        <IconButton className={cx(classes.expandButton, aiGeneratedMessagingOpen ? 'expanded' : undefined)} size={'small'}>
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={aiGeneratedMessagingOpen} unmountOnExit={true}>
        <div className={classes.sectionContent}>
        <AiGeneratedMessaging
          productGroup={productGroup}
          campaignId={campaignId}
          nodes={nodes}
          loading={isFlowChanging || isUpdating}
          linkedinAccount={linkedinAccount}
          customPlaceholders={customPlaceholders}
          fieldInUse={fieldInUse}
          handleSelectNodeFromList={handleSelectNodeFromList}
          handleUnselectNodeFromList={handleUnselectNodeFromList}
        />
        </div>
      </Collapse>
      </div>

      {/* AI Briefing */}
      <div className={cx(classes.section, aiBriefingOpen ? 'expanded' : undefined)}>
      <Stack
        className={classes.sectionHeader}
        direction={'row'}
        alignContent={'center'}
        justifyContent={'space-between'}
        onClick={() => setAiBriefingOpen((prev) => !prev)}
      >
        <Stack direction={'row'} alignContent={'center'} gap={2}>
          <DataObjectIcon className={classes.icon} />
          <Typography className={classes.title}>{t('aiBriefingPlaceholders')}</Typography>
        </Stack>
        <IconButton className={cx(classes.expandButton, aiBriefingOpen ? 'expanded' : undefined)} size={'small'}>
          <ExpandMoreIcon />
        </IconButton>
      </Stack>
      <Collapse in={aiBriefingOpen} unmountOnExit={true}>
        <div className={classes.sectionContent}>
        <UpdateAiBriefing
          productGroup={productGroup}
          offer={normalizedOffer}
          customPlaceholders={customPlaceholders}
          handleAddCustomPlaceholder={handleAddCustomPlaceholder}
          handleDeleteCustomPlaceholder={handleDeleteCustomPlaceholder}
          handleEditCustomPlaceholder={handleEditCustomPlaceholder}
          handleOfferToggleChange={handleOfferToggleChange}
          fieldInUse={fieldInUse}
          handleOfferInputChange={handleOfferInputChange}
        />
        </div>
      </Collapse>
      </div>

      <div className={cx(classes.section, autoReplySettingsOpen ? 'expanded' : undefined)}>
        <Stack
          className={classes.sectionHeader}
          direction={'row'}
          alignContent={'center'}
          justifyContent={'space-between'}
          onClick={() => setAutoReplySettingsOpen((prev) => !prev)}
        >
          <Stack direction="row" alignContent="center" gap={2}>
            <AutoFixHighIcon className={classes.icon} />
            <Typography className={classes.title}>{t('autoReplySettings')}</Typography>
          </Stack>
          <IconButton
            className={cx(classes.expandButton, autoReplySettingsOpen ? 'expanded' : undefined)}
            size={'small'}
            aria-expanded={autoReplySettingsOpen}
            aria-label={'show more'}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
        <Collapse in={autoReplySettingsOpen} unmountOnExit={true}>
          <div className={classes.sectionContent}>
          <AutoReplySettings
            loading={isUpdating}
            isActive={autoReply.isActive ?? false}
            goalOfCommunication={autoReply.goalofCommunication ?? ''}
            caseHandling={autoReply.caseHandling ?? []}
            onIsActiveChange={onIsActiveChange}
            onGoalChange={onGoalChange}
            onCaseHandlingUpdate={onCaseHandlingUpdate}
            handleUpdateAutoReply={handleUpdateAutoReply}
            funnelId={campaignId}
            productGroup={productGroup}
            fieldInUse={fieldInUse}
            customPlaceholders={customPlaceholders}
          />
          </div>
        </Collapse>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '12px',
    overflow: 'hidden',
    '&:not(:last-of-type)': {
      marginBottom: '16px',
    },
    '&.expanded': {
      borderColor: theme.palette.primary.main,
    },
  },
  sectionHeader: {
    padding: '14px 16px',
    background: theme.palette.backgrounds.main_new,
    cursor: 'pointer',
  },
  sectionContent: {
    padding: '16px',
  },
  title: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
  },
  expandButton: {
    padding: '2px',
    pointerEvents: 'none',
    '& .MuiSvgIcon-root': {
      transition: '225ms transform',
      fontSize: theme.typography.pxToRem(24),
    },
    '&.expanded': {
      '& .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
  },
}));

export default CampaignFlowManager;
